import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import './toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export type ToastOptions = {
  id?: string;
  type?: ToastType;
  title?: string;
  message: string;
  /** Auto-dismiss duration in ms. Set 0 to disable auto-dismiss. */
  durationMs?: number;
};

type ToastItem = Required<Pick<ToastOptions, 'message'>> & {
  id: string;
  type: ToastType;
  title?: string;
  createdAt: number;
  durationMs: number;
};

type ToastApi = {
  show: (opts: ToastOptions) => string;
  success: (message: string, opts?: Omit<ToastOptions, 'message' | 'type'>) => string;
  error: (message: string, opts?: Omit<ToastOptions, 'message' | 'type'>) => string;
  warning: (message: string, opts?: Omit<ToastOptions, 'message' | 'type'>) => string;
  info: (message: string, opts?: Omit<ToastOptions, 'message' | 'type'>) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION_MS: Record<ToastType, number> = {
  success: 3000,
  info: 3500,
  warning: 4500,
  error: 6000,
};

const MAX_TOASTS = 6;

function genId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (typeof t === 'number') window.clearTimeout(t);
    timersRef.current.delete(id);
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const clear = useCallback(() => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback(
    (opts: ToastOptions) => {
      const type: ToastType = opts.type ?? 'info';
      const id = opts.id ?? genId();
      const durationMs =
        typeof opts.durationMs === 'number' ? opts.durationMs : DEFAULT_DURATION_MS[type];

      const item: ToastItem = {
        id,
        type,
        title: opts.title,
        message: opts.message,
        createdAt: Date.now(),
        durationMs,
      };

      setToasts((prev) => [item, ...prev].slice(0, MAX_TOASTS));

      if (durationMs > 0) {
        const timer = window.setTimeout(() => dismiss(id), durationMs);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  const api: ToastApi = useMemo(
    () => ({
      show,
      success: (message, opts) => show({ ...opts, type: 'success', message }),
      error: (message, opts) => show({ ...opts, type: 'error', message }),
      warning: (message, opts) => show({ ...opts, type: 'warning', message }),
      info: (message, opts) => show({ ...opts, type: 'info', message }),
      dismiss,
      clear,
    }),
    [show, dismiss, clear]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="toast-viewport" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.type}`}
          role={t.type === 'error' ? 'alert' : 'status'}
          aria-live={t.type === 'error' ? 'assertive' : 'polite'}
        >
          <div className="toast__content">
            {t.title ? <div className="toast__title">{t.title}</div> : null}
            <div className="toast__message">{t.message}</div>
          </div>
          <button className="toast__close" onClick={() => onDismiss(t.id)} aria-label="Close">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

